import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 현장 즐겨찾기 (V320 · 사용자 지시 2026-09-22).
 *
 * <pre>
 *   「현장 즐겨찾기 기능 추가 (본사 기준 담당현장 우선 배치를 위해)」
 * </pre>
 *
 * <h2>왜 서버인가</h2>
 * 브라우저에 두지 않는다({@code useStoredState}). 같은 사람이 사무실 PC 와 현장 패드에서 같은 별을
 * 봐야 하고, 캐시를 비웠다고 사라지면 「눌러 뒀는데 왜 없지」 가 된다. 표는 계정별이다.
 *
 * <h2>순서만 바꾼다</h2>
 * 별은 카드를 위로 올릴 뿐, 무엇이 보이는지는 건드리지 않는다 — 그것은 소속이 정한다. 「내 현장
 * 먼저」({@code siteFirst.ts})와 <b>같은 층</b>의 장치이고, 그쪽이 빈 목록인 본사 직원을 위해 있다.
 *
 * <h2>눌린 것이 곧 보이는 것</h2>
 * 저장을 기다리지 않고 캐시를 먼저 바꾼다. 별 하나 켜는 데 왕복을 기다리면 목록이 한 박자 늦게
 * 튀어 오르고, 그 사이 두 번 누른 사람은 자기가 무엇을 눌렀는지 알 수 없다. 실패하면 되돌린다.
 */
export function useSiteFavorites() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.siteFavorites,
    queryFn: () => apiFetch<string[]>('/v1/site-favorites'),
  });

  const favoriteIds = useMemo(() => new Set(query.data ?? []), [query.data]);

  const toggle = useMutation({
    mutationFn: ({ siteId, favorite }: { siteId: string; favorite: boolean }) =>
      apiFetch<string[]>(`/v1/site-favorites/${siteId}`, { method: 'PUT', body: { favorite } }),
    onMutate: async ({ siteId, favorite }) => {
      // 날아오는 중인 조회가 낙관적 값을 덮어쓰지 않게 먼저 세운다.
      await queryClient.cancelQueries({ queryKey: queryKeys.siteFavorites });
      const previous = queryClient.getQueryData<string[]>(queryKeys.siteFavorites) ?? [];
      const next = favorite ? [...previous.filter((id) => id !== siteId), siteId] : previous.filter((id) => id !== siteId);
      queryClient.setQueryData(queryKeys.siteFavorites, next);
      return { previous };
    },
    onError: (_error, _variables, context) => {
      // 되돌린다. 실패를 화면에 띄우지 않는 것은 <b>되돌아간 별</b>이 이미 그 사실을 말하기 때문이다.
      if (context?.previous) queryClient.setQueryData(queryKeys.siteFavorites, context.previous);
    },
    // 서버가 돌려준 목록이 정본이다 — 다른 기기에서 켠 별도 이때 함께 따라온다.
    onSuccess: (list) => queryClient.setQueryData(queryKeys.siteFavorites, list),
  });

  return {
    favoriteIds,
    setFavorite: (siteId: string, favorite: boolean) => toggle.mutate({ siteId, favorite }),
  };
}
